import { useEffect, useMemo, useState } from 'react';
import { Button, Row, Col, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { uploadItemFiles, getItemAttachments, deleteAttachment } from '../../services/Items/UploadFiles';
import { getItemFolioRanges } from '../../services/Items/Inventory';

function UploadFilesModal({ show, handleClose, itemId, itemName, onSuccess }) {
    const [folioRanges, setFolioRanges] = useState([]);
    const [folioOptions, setFolioOptions] = useState([]);
    const [selectedFolioId, setSelectedFolioId] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [existingFiles, setExistingFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [saving, setSaving] = useState(false);

    const allowedFileTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const hasSingleFolio = folioOptions.length === 1;

    const formatFolioRange = (r) => {
        const isSingle = Number(r.count || 0) <= 1 || r.startFolioName === r.endFolioName;
        return isSingle
            ? r.startFolioName
            : `${r.startFolioName} – ${r.endFolioName} (${r.count})`;
    };

    const selectedFolioName = useMemo(() => {
        const found = folioOptions.find(f => String(f.id) === String(selectedFolioId));
        return found?.name || '';
    }, [folioOptions, selectedFolioId]);

    const loadAttachments = async (folioId = null) => {
        if (!itemId) return;
        setLoadingFiles(true);
        const files = await getItemAttachments(itemId, folioId || null);
        setExistingFiles(files);
        setLoadingFiles(false);
    };

    useEffect(() => {
        if (!show || !itemId) return;

        setSelectedFiles([]);
        setExistingFiles([]);
        setSelectedFolioId('');
        setFolioRanges([]);
        setFolioOptions([]);

        (async () => {
            const ranges = await getItemFolioRanges(itemId);
            setFolioRanges(ranges || []);

            const expanded = [];
            (ranges || []).forEach((r) => {
                if (Array.isArray(r.folios) && r.folios.length) {
                    r.folios.forEach((f) => expanded.push(f));
                } else if (r.startFolioId && r.startFolioName && Number(r.count) === 1) {
                    expanded.push({ id: r.startFolioId, name: r.startFolioName });
                }
            });

            setFolioOptions(expanded);

            if (expanded.length === 1) {
                setSelectedFolioId(String(expanded[0].id));
                await loadAttachments(expanded[0].id);
            } else {
                await loadAttachments(null);
            }
        })();
    }, [show, itemId]);

    useEffect(() => {
        if (!show || !itemId) return;
        if (!selectedFolioId) return;
        loadAttachments(selectedFolioId);
    }, [selectedFolioId]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        const invalidFiles = files.filter(file => !allowedFileTypes.includes(file.type));

        if (invalidFiles.length > 0) {
            toast.error('Algunos archivos tienen formatos no permitidos.');
            return;
        }

        setSelectedFiles(files);
    };

    const handleUpload = async () => {
        try {
            if (!selectedFiles.length) {
                toast.warning('Selecciona al menos un archivo.');
                return;
            }

            if (folioOptions.length > 1 && !selectedFolioId) {
                toast.warning('Selecciona el folio al que corresponde la evidencia.');
                return;
            }

            setSaving(true);

            const result = await uploadItemFiles(
                itemId,
                selectedFiles,
                selectedFolioId || null
            );

            if (!result?.result) {
                toast.error(result?.message || 'No se pudieron subir los archivos.');
                return;
            }

            toast.success(result?.message || 'Archivos subidos correctamente.');
            setSelectedFiles([]);
            await loadAttachments(selectedFolioId || null);
            onSuccess?.();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (attachmentId) => {
        const result = await deleteAttachment(attachmentId);
        if (result?.result) {
            toast.success('Archivo eliminado correctamente.');
            await loadAttachments(selectedFolioId || null);
            onSuccess?.();
        } else {
            toast.error('No se pudo eliminar el archivo.');
        }
    };

    const renderType = (mimeType = '', fileType = '') => {
        if (fileType === 'image' || mimeType.startsWith('image/')) return 'Imagen';
        return 'Documento';
    };

    return (
        <Modal size="lg" show={show} onHide={handleClose} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Subir evidencia</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Row className="mb-3">
                    <Col md={12}>
                        <Alert variant="secondary" className="mb-0">
                            <div><strong>Producto:</strong> {itemName || `Item #${itemId}`}</div>
                            {/* <div><strong>Item ID:</strong> {itemId}</div> */}
                            {folioRanges.length > 0 && (
                                <div className="mt-2">
                                    <strong>Folios asignados:</strong>
                                    <ul className="mb-0 mt-1">
                                        {folioRanges.map((r, idx) => (
                                            <li key={idx}>{formatFolioRange(r)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Alert>
                    </Col>
                </Row>

                {folioOptions.length > 0 && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-bold">Folio de hacienda</Form.Label>
                                <Form.Select
                                    value={selectedFolioId}
                                    onChange={(e) => setSelectedFolioId(e.target.value)}
                                    disabled={hasSingleFolio}
                                >
                                    <option value="">Selecciona un folio</option>
                                    {folioOptions.map((f) => (
                                        <option key={f.id} value={f.id}>{f.name}</option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    La evidencia quedará ligada al folio seleccionado.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {!!selectedFolioName && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <Badge bg="info">Folio seleccionado: {selectedFolioName}</Badge>
                        </Col>
                    </Row>
                )}

                <Row className="mb-3">
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label className="fw-bold">Archivos</Form.Label>
                            <Form.Control
                                type="file"
                                multiple
                                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.xls,.xlsx,.doc,.docx"
                                onChange={handleFileChange}
                            />
                            <Form.Text className="text-muted">
                                Formatos permitidos: imágenes, PDF, Excel y Word.
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                {selectedFiles.length > 0 && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="border rounded p-2 bg-light">
                                <strong>Archivos seleccionados:</strong>
                                <ul className="mb-0 mt-2">
                                    {selectedFiles.map((file, idx) => (
                                        <li key={idx}>
                                            {file.name} ({(file.size / 1024).toFixed(2)} KB)
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Col>
                    </Row>
                )}

                <hr />

                <Row>
                    <Col md={12}>
                        <div className="fw-bold mb-2">Evidencias registradas</div>

                        {loadingFiles ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" /> Cargando...
                            </div>
                        ) : existingFiles.length > 0 ? (
                            <div className="border rounded p-3">
                                {existingFiles.map((file) => (
                                    <Row key={file.id} className="align-items-center mb-2">
                                        <Col md={7}>
                                            <div><strong>{file.original_name || file.name}</strong></div>
                                            <small className="text-muted">
                                                {renderType(file.mime_type, file.file_type)} · {(Number(file.size || 0) / 1024).toFixed(2)} KB
                                            </small>
                                        </Col>
                                        <Col md={5} className="text-end">
                                            <Button
                                                size="sm"
                                                variant="outline-info"
                                                className="me-2"
                                                href={file.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Ver
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => handleDelete(file.id)}
                                            >
                                                Eliminar
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted">No hay evidencias registradas.</div>
                        )}
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cerrar
                </Button>
                <Button variant="success" onClick={handleUpload} disabled={saving}>
                    {saving ? 'Subiendo...' : 'Subir'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default UploadFilesModal;